/**
 * PR-35D — Team Theme Engine.
 * Lets users personalize accent colors based on a favorite national team
 * or a fully manual palette. The selection is persisted to localStorage
 * (and may be mirrored to the profile / database by the consumer) and is
 * applied to CSS variables consumed by buttons, gradients, bars and charts.
 * Does NOT remove or alter any other functionality. Demo mode untouched.
 */
import { trackEvent } from "@/lib/audit";

export type TeamId =
  | "MEX" | "ARG" | "BRA" | "COL" | "USA"
  | "ESP" | "FRA" | "GER" | "ITA" | "POR"
  | "ENG" | "URU" | "CHI" | "ECU" | "JPN"
  | "CUSTOM" | "DEFAULT";

export interface TeamPalette {
  id: TeamId;
  label: string;
  flag: string;
  /** main accent */
  primary: string;
  /** secondary accent */
  secondary: string;
  /** tertiary / highlight */
  tertiary: string;
  /** linear-gradient string */
  gradient: string;
}

export const TEAM_PALETTES: TeamPalette[] = [
  { id: "DEFAULT", label: "VerseSport", flag: "✨", primary: "oklch(0.82 0.24 145)", secondary: "oklch(0.65 0.22 265)", tertiary: "oklch(0.78 0.18 60)", gradient: "linear-gradient(135deg, oklch(0.82 0.24 145), oklch(0.65 0.22 265))" },
  { id: "MEX", label: "México",        flag: "🇲🇽", primary: "#006847", secondary: "#FFFFFF", tertiary: "#CE1126", gradient: "linear-gradient(135deg,#006847,#FFFFFF,#CE1126)" },
  { id: "ARG", label: "Argentina",     flag: "🇦🇷", primary: "#75AADB", secondary: "#FFFFFF", tertiary: "#F6B40E", gradient: "linear-gradient(135deg,#75AADB,#FFFFFF,#75AADB)" },
  { id: "BRA", label: "Brasil",        flag: "🇧🇷", primary: "#009C3B", secondary: "#FFDF00", tertiary: "#002776", gradient: "linear-gradient(135deg,#009C3B,#FFDF00,#002776)" },
  { id: "COL", label: "Colombia",      flag: "🇨🇴", primary: "#FCD116", secondary: "#003893", tertiary: "#CE1126", gradient: "linear-gradient(135deg,#FCD116,#003893,#CE1126)" },
  { id: "USA", label: "Estados Unidos",flag: "🇺🇸", primary: "#3C3B6E", secondary: "#B22234", tertiary: "#FFFFFF", gradient: "linear-gradient(135deg,#3C3B6E,#B22234,#FFFFFF)" },
  { id: "ESP", label: "España",        flag: "🇪🇸", primary: "#AA151B", secondary: "#F1BF00", tertiary: "#AA151B", gradient: "linear-gradient(135deg,#AA151B,#F1BF00,#AA151B)" },
  { id: "FRA", label: "Francia",       flag: "🇫🇷", primary: "#0055A4", secondary: "#FFFFFF", tertiary: "#EF4135", gradient: "linear-gradient(135deg,#0055A4,#FFFFFF,#EF4135)" },
  { id: "GER", label: "Alemania",      flag: "🇩🇪", primary: "#000000", secondary: "#DD0000", tertiary: "#FFCE00", gradient: "linear-gradient(135deg,#000000,#DD0000,#FFCE00)" },
  { id: "ITA", label: "Italia",        flag: "🇮🇹", primary: "#008C45", secondary: "#F4F5F0", tertiary: "#CD212A", gradient: "linear-gradient(135deg,#008C45,#F4F5F0,#CD212A)" },
  { id: "POR", label: "Portugal",      flag: "🇵🇹", primary: "#046A38", secondary: "#DA291C", tertiary: "#FFD200", gradient: "linear-gradient(135deg,#046A38,#DA291C,#FFD200)" },
  { id: "ENG", label: "Inglaterra",    flag: "🏴", primary: "#FFFFFF", secondary: "#CE1124", tertiary: "#012169", gradient: "linear-gradient(135deg,#FFFFFF,#CE1124,#012169)" },
  { id: "URU", label: "Uruguay",       flag: "🇺🇾", primary: "#0038A8", secondary: "#FFFFFF", tertiary: "#FCD116", gradient: "linear-gradient(135deg,#0038A8,#FFFFFF,#FCD116)" },
  { id: "CHI", label: "Chile",         flag: "🇨🇱", primary: "#D52B1E", secondary: "#FFFFFF", tertiary: "#0033A0", gradient: "linear-gradient(135deg,#D52B1E,#FFFFFF,#0033A0)" },
  { id: "ECU", label: "Ecuador",       flag: "🇪🇨", primary: "#FFD100", secondary: "#0072CE", tertiary: "#EF3340", gradient: "linear-gradient(135deg,#FFD100,#0072CE,#EF3340)" },
  { id: "JPN", label: "Japón",         flag: "🇯🇵", primary: "#BC002D", secondary: "#FFFFFF", tertiary: "#BC002D", gradient: "linear-gradient(135deg,#BC002D,#FFFFFF,#BC002D)" },
];

const LS_TEAM_KEY = "vs.pr35d.favoriteTeam";
const LS_CUSTOM_KEY = "vs.pr35d.customPalette";
const LS_ACTIVE_KEY = "vs.pr35d.activeThemeId";

export interface CustomPalette {
  primary: string;
  secondary: string;
  tertiary: string;
}

function safeStorage(): Storage | null {
  try { return typeof window !== "undefined" ? window.localStorage : null; } catch { return null; }
}

export function getFavoriteTeam(): TeamId {
  const s = safeStorage();
  const raw = s?.getItem(LS_TEAM_KEY) as TeamId | null;
  return raw ?? "DEFAULT";
}

export function getActiveThemeId(): TeamId {
  const s = safeStorage();
  return ((s?.getItem(LS_ACTIVE_KEY) as TeamId | null) ?? getFavoriteTeam()) || "DEFAULT";
}

export function getCustomPalette(): CustomPalette | null {
  const s = safeStorage();
  const raw = s?.getItem(LS_CUSTOM_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw) as CustomPalette; } catch { return null; }
}

export function getPaletteById(id: TeamId): TeamPalette {
  return TEAM_PALETTES.find((p) => p.id === id) ?? TEAM_PALETTES[0];
}

/** Apply a palette to CSS variables. Returns the palette that was applied. */
export function applyTeamPalette(id: TeamId, custom?: CustomPalette | null): TeamPalette {
  const base = id === "CUSTOM" && custom
    ? { id: "CUSTOM" as TeamId, label: "Personalizado", flag: "🎨",
        primary: custom.primary, secondary: custom.secondary, tertiary: custom.tertiary,
        gradient: `linear-gradient(135deg,${custom.primary},${custom.secondary},${custom.tertiary})` }
    : getPaletteById(id);
  if (typeof document !== "undefined") {
    const r = document.documentElement.style;
    r.setProperty("--team-primary", base.primary);
    r.setProperty("--team-secondary", base.secondary);
    r.setProperty("--team-tertiary", base.tertiary);
    r.setProperty("--team-gradient", base.gradient);
  }
  const s = safeStorage();
  s?.setItem(LS_ACTIVE_KEY, base.id);
  void trackEvent("theme_palette_applied", { team: base.id, label: base.label });
  return base;
}

export function setFavoriteTeam(id: TeamId): TeamPalette {
  const s = safeStorage();
  s?.setItem(LS_TEAM_KEY, id);
  void trackEvent("favorite_team_selected", { team: id });
  const palette = applyTeamPalette(id);
  void trackEvent("theme_changed", { team: id, source: "favorite_team" });
  return palette;
}

export function setCustomPalette(p: CustomPalette): TeamPalette {
  const s = safeStorage();
  s?.setItem(LS_CUSTOM_KEY, JSON.stringify(p));
  const applied = applyTeamPalette("CUSTOM", p);
  void trackEvent("theme_changed", { team: "CUSTOM", source: "manual" });
  return applied;
}

export function resetTheme(): TeamPalette {
  const s = safeStorage();
  s?.removeItem(LS_TEAM_KEY);
  s?.removeItem(LS_CUSTOM_KEY);
  s?.removeItem(LS_ACTIVE_KEY);
  const applied = applyTeamPalette("DEFAULT");
  void trackEvent("theme_changed", { team: "DEFAULT", source: "reset" });
  return applied;
}

export function ensureThemeBootstrapped(): void {
  const id = getActiveThemeId();
  const custom = id === "CUSTOM" ? getCustomPalette() : null;
  applyTeamPalette(id, custom);
}